/** Warm-up sequence message types and helpers. */

import { isGhlIntegrationActive } from "@/lib/ghlIntegrationActive";

export const STORAGE_KEY = "warm_up_sequences_draft_v2";
const LEGACY_STORAGE_KEY = "warm_up_sequences_draft_v1";

export const createEmptyWarmUpMessage = () => ({
  id:
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  delay_days: 0,
  delay_hours: 0,
  delay_minutes: 0,
  text: "",
  enabled: true,
  ai_corrected: false,
});

export const DEFAULT_WARM_UP_MESSAGES = [];

export function normalizeWarmUpMessage(m) {
  const base = createEmptyWarmUpMessage();
  return {
    id: m?.id || base.id,
    delay_days: Math.max(0, Math.trunc(Number(m?.delay_days)) || 0),
    delay_hours: Math.max(0, Math.trunc(Number(m?.delay_hours)) || 0),
    delay_minutes: Math.max(0, Math.trunc(Number(m?.delay_minutes)) || 0),
    text: typeof m?.text === "string" ? m.text : "",
    enabled: m?.enabled !== false,
    ai_corrected: m?.ai_corrected === true,
  };
}

export const WARM_UP_IMMEDIATE_DELAY_TEXT =
  "Delay is set to 0 days, 0 hours, and 0 minutes. This message is sent immediately after Warm Up is turned on.";

export function warmUpDelayIsImmediate(message) {
  const days = Math.max(0, Math.trunc(Number(message?.delay_days)) || 0);
  const hours = Math.max(0, Math.trunc(Number(message?.delay_hours)) || 0);
  const minutes = Math.max(0, Math.trunc(Number(message?.delay_minutes)) || 0);
  return days === 0 && hours === 0 && minutes === 0;
}

export function describeWarmUpPageHeadline({
  pitKind,
  sequencesLoading,
  testModeActive,
  triggerText,
}) {
  if (pitKind === "loading" || sequencesLoading) return "";
  if (
    pitKind === "ready" &&
    testModeActive === false &&
    triggerText === "Automatic stage triggers are ready."
  ) {
    return "Warm Up is ready. PIT connection is active, automatic stage triggers are ready, test mode is off.";
  }
  return "";
}

export function formatWarmUpSavedAt(value, locale, timeZone) {
  if (typeof value !== "string" || value.trim() === "") return "";
  const trimmed = value.trim();
  let parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    parsed = new Date(trimmed.replace(/\.(\d{3})\d+/, ".$1"));
  }
  if (Number.isNaN(parsed.getTime())) return "";
  const options = {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  };
  if (typeof timeZone === "string" && timeZone !== "") options.timeZone = timeZone;
  return new Intl.DateTimeFormat(locale, options).format(parsed);
}

export function formatWarmUpMessageDelayLabel(message) {
  if (warmUpDelayIsImmediate(message)) return "Sent immediately";
  const days = Math.max(0, Math.trunc(Number(message?.delay_days)) || 0);
  const hours = Math.max(0, Math.trunc(Number(message?.delay_hours)) || 0);
  const minutes = Math.max(0, Math.trunc(Number(message?.delay_minutes)) || 0);
  const parts = [];
  if (days > 0) parts.push(`${days} ${days === 1 ? "day" : "days"}`);
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
  return `Sent after ${parts.join(" ")}`;
}

export const WARM_UP_SEQUENCE_ACCEPTANCE_NOTE =
  "Sent means HighLevel accepted the request. It does not confirm that the contact received the message.";

export const WARM_UP_MANUAL_PIT_RULE =
  "Sending messages and turning Warm Up on manually require an active PIT connection.";

export function normalizeWarmUpMessages(messages) {
  if (!Array.isArray(messages)) return [];
  return messages.map(normalizeWarmUpMessage);
}

export const WARM_UP_EMPTY_TEXT_MESSAGE = "Empty message text is not allowed.";

export function classifyWarmUpEmptyText(storedMessage, incomingText) {
  const text = typeof incomingText === "string" ? incomingText : "";
  if (text.trim() !== "") return "not_empty";
  if (!storedMessage || typeof storedMessage !== "object") return "new_empty";
  const storedText = storedMessage.text;
  if (typeof storedText === "string" && text === storedText && storedText.trim() === "") {
    return "unchanged_historical_empty";
  }
  if (typeof storedText === "string" && storedText.trim() !== "") return "cleared";
  return "new_empty";
}

export function warmUpEmptyTextError(storedMessages, message) {
  const stored = Array.isArray(storedMessages)
    ? storedMessages.find((item) => item && item.id === message?.id)
    : undefined;
  const kind = classifyWarmUpEmptyText(stored, message?.text);
  return kind === "new_empty" || kind === "cleared" ? WARM_UP_EMPTY_TEXT_MESSAGE : "";
}

/** One-time migration: read legacy localStorage draft only. */
export function loadLegacyWarmUpDraftFromLocalStorage() {
  try {
    let raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    }
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return normalizeWarmUpMessages(parsed);
  } catch {
    return null;
  }
}

export function clearWarmUpLocalStorageDraft() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function getWarmUpSequencesApiUrl(backendUrl) {
  const base = (backendUrl || "").replace(/\/$/, "");
  return `${base}/api/warm-up-sequences`;
}

function pitLocationId(data) {
  const location = data?.integration?.credentials?.location_id;
  return typeof location === "string" ? location.trim() : "";
}

/**
 * Warm Up PIT is an active integration with a PIT api key and location.
 * A stored GHL row, a Marketplace app connection, or only one of the two PIT
 * fields does not make selectors or sending available.
 */
export function warmUpIntegrationLocationSaved(data) {
  return pitLocationId(data) !== "";
}

export function warmUpTestModeActive(data) {
  return data?.integration?.test_mode?.activate === true;
}

export function describeWarmUpInboundWorkflow({ integrationPresent, locationSaved }) {
  if (!integrationPresent) {
    return "Incoming stage events cannot update Warm Up. GoHighLevel is not connected.";
  }
  if (!locationSaved) {
    return "Incoming stage events cannot update Warm Up until a location is saved.";
  }
  return "Stage changes from HighLevel turn Warm Up on or off even without an active PIT connection.";
}

export const WARM_UP_ACCEPTANCE_NOTE =
  "A completed Warm Up send means the API accepted the request. It does not confirm that the contact received the message.";

const WARM_UP_SEND_OUTCOME_LABELS = {
  sent: "Accepted by API",
  uncertain: "Acceptance unknown",
  error: "Not accepted",
  blocked: "Not accepted",
  failed: "Not accepted",
  pending: "Not sent",
  paused: "Not sent",
  leased: "Not sent",
  cancelled: "Not sent",
};

export function describeWarmUpSendOutcome(status) {
  if (Object.prototype.hasOwnProperty.call(WARM_UP_SEND_OUTCOME_LABELS, status)) {
    return WARM_UP_SEND_OUTCOME_LABELS[status];
  }
  return "Not sent";
}

export function describeWarmUpManualSends(pitReady) {
  if (pitReady) {
    return "Manual Warm Up and sending require an active PIT connection. That connection is active.";
  }
  return "Manual Warm Up and sending require an active PIT connection. That connection is not active.";
}

export function describeWarmUpTestMode(testModeActive) {
  if (testModeActive) {
    return "Test mode is on. Warm Up sending is paused for every contact. The Digital Brain test-contact list does not allow Warm Up sends.";
  }
  return "Test mode is off and is not pausing Warm Up sending.";
}

export function resolveWarmUpPitReadiness(data) {
  if (!data || typeof data !== "object") {
    return { pitReady: false, kind: "missing" };
  }
  const hasRecord = Boolean(data.id) || Boolean(data.integration);
  const hasApiKey = data.has_api_key === true;
  const locationId = pitLocationId(data);
  if (isGhlIntegrationActive(data) && locationId) {
    return { pitReady: true, kind: "ready" };
  }
  if (data.has_app === true && !hasApiKey) {
    return { pitReady: false, kind: "marketplace_only" };
  }
  if (!hasRecord) {
    return { pitReady: false, kind: "missing" };
  }
  if (hasApiKey || locationId) {
    return { pitReady: false, kind: "incomplete" };
  }
  return { pitReady: false, kind: "placeholder" };
}

export function warmUpTriggersAreReady(sequences) {
  return sequences?.ghl_triggers?.trigger_config_status === "ready";
}

export const WARM_UP_SAME_PAIR_MESSAGE =
  "Enable and disable are the same pipeline and stage. Choose a different stage before saving.";

/**
 * A complete pair is one pipeline id plus one stage id.
 * The same pair cannot be both enable and disable.
 */
export function warmUpStagePairsMatch({
  enablePipelineId,
  enableStageId,
  disablePipelineId,
  disableStageId,
}) {
  const enablePipeline = storedPairId(enablePipelineId);
  const enableStage = storedPairId(enableStageId);
  const disablePipeline = storedPairId(disablePipelineId);
  const disableStage = storedPairId(disableStageId);
  return Boolean(
    enablePipeline &&
      enableStage &&
      disablePipeline &&
      disableStage &&
      enablePipeline === disablePipeline &&
      enableStage === disableStage
  );
}

/**
 * Automation is ready only when enable and disable are two different pipeline/stage pairs.
 * A single saved pair stays partial and does not turn automatic triggers on.
 */
export function describeWarmUpTriggerAutomation({
  enablePipelineId,
  enableStageId,
  disablePipelineId,
  disableStageId,
}) {
  const enableSet = Boolean(storedPairId(enablePipelineId) && storedPairId(enableStageId));
  const disableSet = Boolean(storedPairId(disablePipelineId) && storedPairId(disableStageId));
  const samePair = warmUpStagePairsMatch({
    enablePipelineId,
    enableStageId,
    disablePipelineId,
    disableStageId,
  });
  if (enableSet && disableSet && !samePair) {
    return "Automatic stage triggers are ready.";
  }
  if (samePair) {
    return "Automatic stage triggers are not ready. Enable and disable are the same stage, so automatic triggers stay off.";
  }
  if (enableSet) {
    return "Automatic stage triggers are not ready. Only the enable pair is set, so automatic triggers stay off.";
  }
  if (disableSet) {
    return "Automatic stage triggers are not ready. Only the disable pair is set, so automatic triggers stay off.";
  }
  return "Automatic stage triggers are not ready.";
}

function revisionOrZero(value) {
  return Number.isInteger(value) && value >= 0 ? value : 0;
}

function storedPairId(value) {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Read the existing PUT validation body. FastAPI sends either a string detail
 * or a list of {msg} items. An empty result means the failure had no validation text.
 */
export function readWarmUpValidationMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail.trim();
  if (!Array.isArray(detail)) return "";
  return detail
    .map((item) => (item && typeof item.msg === "string" ? item.msg.trim() : ""))
    .filter(Boolean)
    .join(" ");
}

export function warmUpSaveIsRevisionConflict(error) {
  return error?.response?.status === 409;
}

export function warmUpValidationTargetsSamePair(message) {
  return (
    typeof message === "string" &&
    message.includes("enable and disable stage pairs must be different")
  );
}

export function readWarmUpTriggerDraft(sequences) {
  const triggers = sequences?.ghl_triggers;
  const enable = triggers?.enable;
  const disable = triggers?.disable;
  return {
    pipelineId: storedPairId(enable?.pipeline_id),
    stage: {
      id: storedPairId(enable?.stage_id),
      name: typeof enable?.stage_name === "string" ? enable.stage_name : "",
    },
    disablePipelineId: storedPairId(disable?.pipeline_id),
    disableStage: {
      id: storedPairId(disable?.stage_id),
      name: typeof disable?.stage_name === "string" ? disable.stage_name : "",
    },
    messageRevision: revisionOrZero(sequences?.message_config_revision),
    triggerRevision: revisionOrZero(triggers?.config_revision),
    triggerStatus:
      triggers?.trigger_config_status === "ready" ||
      triggers?.trigger_config_status === "incomplete"
        ? triggers.trigger_config_status
        : "",
    savedAt: typeof triggers?.saved_at === "string" ? triggers.saved_at : "",
  };
}

export function stageOnWarmUpPipeline(pipelines, pipelineId, stage) {
  const pipeline = (pipelines || []).find((item) => item && item.id === pipelineId);
  const stageId = typeof stage?.id === "string" ? stage.id.trim() : "";
  if (!pipeline || !stageId) return { id: "", name: "" };
  const match = (pipeline.stages || []).find((item) => item.id === stageId);
  if (!match) return { id: "", name: "" };
  return { id: match.id, name: typeof match.name === "string" ? match.name : "" };
}

/**
 * Keep a saved stage when its ID leaves the catalog.
 * A matching ID takes the catalog's current name. Another stage with the same name is not chosen.
 */
export function retainWarmUpSavedStage(pipelines, pipelineId, stage) {
  const current = stage && typeof stage === "object" ? stage : { id: "", name: "" };
  const stageId = storedPairId(current.id);
  if (!stageId) return current;
  const pipeline = (pipelines || []).find((item) => item && item.id === pipelineId);
  if (!pipeline) return current;
  const match = (pipeline.stages || []).find((item) => item && item.id === stageId);
  if (!match) return current;
  const name = typeof match.name === "string" ? match.name : "";
  if (current.id === match.id && current.name === name) return current;
  return { id: match.id, name };
}

function exactStageName(value) {
  return typeof value === "string" && value !== "" ? value : "";
}

/**
 * Q58. Stage ID matches by ID. A payload without Stage ID matches the saved
 * pipeline_id and exact stage name. This does not take a catalog.
 */
export function matchWarmUpStageEvent(savedPair, event) {
  const savedPipeline = storedPairId(savedPair?.pipeline_id);
  const eventPipeline = storedPairId(event?.pipeline_id);
  if (!savedPipeline || savedPipeline !== eventPipeline) return false;
  const eventStageId = storedPairId(event?.stage_id);
  if (eventStageId) return eventStageId === storedPairId(savedPair?.stage_id);
  const incomingName = exactStageName(event?.pipeline_stage);
  const savedName = exactStageName(savedPair?.stage_name);
  if (!incomingName || !savedName) return false;
  return incomingName === savedName;
}

export function describeWarmUpCatalogMismatch({ section, pipelines, pipelineId, stage }) {
  const label = section === "disable" ? "disable" : "enable";
  const pipelineKey = storedPairId(pipelineId);
  const stageId = storedPairId(stage?.id);
  const stageName = exactStageName(stage?.name);
  if (!pipelineKey && !stageId && !stageName) return "";
  const pipeline = (pipelines || []).find((item) => item && item.id === pipelineKey);
  if (pipelineKey && !pipeline) {
    const stagePart = stageId ? ` and stage ${stageId}` : "";
    return `Saved ${label} pipeline ${pipelineKey}${stagePart} is no longer in the catalog. Choose a current pipeline. No other pipeline was selected by name.`;
  }
  if (!pipeline) return "";
  if (stageId) {
    const match = (pipeline.stages || []).find((item) => item && item.id === stageId);
    if (match) return "";
    const savedLabel = stageName ? `${stageName} (${stageId})` : stageId;
    return `Saved ${label} stage ${savedLabel} is no longer in the catalog. Choose a current stage. No other stage was selected by name.`;
  }
  if (stageName) {
    return `Saved ${label} stage name ${stageName} has no Stage ID. It is not matched to a catalog stage.`;
  }
  return "";
}

export function warmUpDisplayedTriggerIds({
  catalogStatus,
  pipelines,
  enablePipelineId,
  enableStage,
  disablePipelineId,
  disableStage,
}) {
  const pipelineShown = (pipelineId) => {
    const id = storedPairId(pipelineId);
    if (catalogStatus === "empty") return "";
    if (catalogStatus !== "ready") return id;
    return (pipelines || []).some((item) => item && item.id === id) ? id : "";
  };
  const stageShown = (pipelineId, stage) => {
    if (catalogStatus === "empty") return "";
    if (catalogStatus !== "ready") return storedPairId(stage?.id);
    return stageOnWarmUpPipeline(pipelines, pipelineId, stage).id;
  };
  return {
    enablePipelineId: pipelineShown(enablePipelineId),
    enableStageId: stageShown(enablePipelineId, enableStage),
    disablePipelineId: pipelineShown(disablePipelineId),
    disableStageId: stageShown(disablePipelineId, disableStage),
  };
}

function pairFromCatalog(pipelines, pipelineId, stage) {
  const selected = typeof pipelineId === "string" ? pipelineId.trim() : "";
  if (!selected) return undefined;
  const pipeline = (pipelines || []).find((item) => item && item.id === selected);
  if (!pipeline) return null;
  const kept = stageOnWarmUpPipeline(pipelines, pipeline.id, stage);
  const pair = {
    pipeline_id: pipeline.id,
    pipeline_name: typeof pipeline.name === "string" ? pipeline.name : "",
  };
  if (kept.id) {
    pair.stage_id = kept.id;
    pair.stage_name = kept.name;
  }
  return pair;
}

/**
 * Enable and disable choices for PUT /api/warm-up-sequences.
 * Each id comes from the loaded catalog on its own. An empty choice omits that pair.
 * A selected id that is not in the catalog is omitted so another pipeline is not substituted.
 */
export function warmUpEnableTriggerPayload({
  integrationId,
  pipelineId,
  pipelines,
  stage,
  disablePipelineId,
  disableStage,
}) {
  const integration = typeof integrationId === "string" ? integrationId.trim() : "";
  if (!integration) return null;
  const enable = pairFromCatalog(pipelines, pipelineId, stage);
  const disable = pairFromCatalog(pipelines, disablePipelineId, disableStage);
  if (enable === null || disable === null) return null;
  const payload = { integration_id: integration };
  if (enable) payload.enable = enable;
  if (disable) payload.disable = disable;
  return payload;
}

function catalogId(value) {
  return typeof value === "string" ? value.trim() : "";
}

function catalogName(value) {
  return typeof value === "string" ? value : "";
}

/**
 * Keep only pipeline and stage ids and names from POST /api/integrations/ghl/list_pipelines.
 * A missing pipelines array is unusable. An empty array is a real empty catalog.
 */
export function normalizeWarmUpPipelineCatalog(data) {
  if (!data || typeof data !== "object" || !Array.isArray(data.pipelines)) return null;
  return data.pipelines.flatMap((pipeline) => {
    if (!pipeline || typeof pipeline !== "object") return [];
    const id = catalogId(pipeline.id);
    if (!id) return [];
    const stages = Array.isArray(pipeline.stages)
      ? pipeline.stages.flatMap((stage) => {
          if (!stage || typeof stage !== "object") return [];
          const stageId = catalogId(stage.id);
          if (!stageId) return [];
          return [{ id: stageId, name: catalogName(stage.name) }];
        })
      : [];
    return [{ id, name: catalogName(pipeline.name), stages }];
  });
}
